import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Table,
  Tag,
  Input,
  Space,
  Typography,
  Segmented,
  Button,
  message,
  Progress,
  Tooltip,
  Modal,
  Descriptions,
} from 'antd';
import {
  ApartmentOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  AuditOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { TERMIN_HAK, terminIpucu as ortakTerminIpucu } from '../../utils/termin';
import YsOnayModal from './YsOnayModal';

// YS onay/red işleminin anlamlı olduğu (kurulum öncesi) süreç durumları
const YS_ONAY_DURUMLARI = new Set(['Talep', 'Planlamada', 'İzin Bekliyor', 'İzin Reddedildi']);

const { Title } = Typography;

const SUREC_RENK = {
  'İzin Bekliyor': { color: 'volcano', icon: <ToolOutlined /> },
  'İzin Reddedildi': { color: 'red', icon: <ToolOutlined /> },
  'Talep': { color: 'gold', icon: <ToolOutlined /> },
  'Planlamada': { color: 'blue', icon: <ToolOutlined /> },
  'Kurulum Bekliyor': { color: 'orange', icon: <ToolOutlined /> },
  'Enerji Sürecinde': { color: 'cyan', icon: <ThunderboltOutlined /> },
  // 20.08 madde 8: red/iptal sürecin sonu — ayrı durum olarak görünür
  'Enerji Reddedildi': { color: 'red', icon: <ThunderboltOutlined /> },
  'Enerji İptal': { color: 'red', icon: <ThunderboltOutlined /> },
  'Tamamlandı': { color: 'green', icon: <CheckCircleOutlined /> },
};

// Detaylı süreç durumunu üst sayaç/filtre için ana gruba indirger
const ANA_GRUP = {
  'İzin Bekliyor': 'İnşaat Sürecinde',
  'İzin Reddedildi': 'İnşaat Sürecinde',
  'Talep': 'İnşaat Sürecinde',
  'Planlamada': 'İnşaat Sürecinde',
  'Kurulum Bekliyor': 'İnşaat Sürecinde',
  'Enerji Sürecinde': 'Enerji Sürecinde',
  'Enerji Reddedildi': 'Enerji Sürecinde',
  'Enerji İptal': 'Enerji Sürecinde',
  'Tamamlandı': 'Tamamlandı',
};
const anaGrup = (durum) => ANA_GRUP[durum] || durum;

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('tr-TR');
};

// Sıralama için zaman değeri. Boş/geçersiz tarihler `new Date(0)` ile 1970'e
// düşüp listenin ortasına karışmasın diye -Infinity döner (azalan sıralamada
// en alta iner).
const zamanDegeri = (value) => {
  if (!value) return -Infinity;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? -Infinity : t;
};

// --- Termin (deadline) hesabı ---
// İnşaat aşamaları için 7 gün; enerji aşamasında ADIM BAZLI termin uygulanır.
const TERMIN_GUN = 7; // inşaat süreçleri için verilen termin (gün)

// Enerji adımlarının termin süreleri — backend'deki tek kaynakla aynı:
// src/backend/services/enerji_surec.py → ADIMLAR[].termin_gun
const ENERJI_ADIM_TERMIN = {
  kesif: 7,
  enerji_tipi: 0,
  enerji_talebi: 7,
  proje_onayi: 5,
  baglanti_gorusu: 7,
  ruhsat: 35,
  kazi: 30,
  pano: 7,
  muayene: 15,
};

const ENERJI_ADIM_AD = {
  kesif: 'Keşif',
  enerji_tipi: 'Enerji Tipi',
  enerji_talebi: 'Enerji Talebi',
  proje_onayi: 'Proje Onayı',
  baglanti_gorusu: 'Bağlantı Görüşü',
  ruhsat: 'Ruhsat',
  kazi: 'Kazı',
  pano: 'Pano',
  muayene: 'Muayene',
};

// Termin ipucu ortak yardımcıdan gelir (src/utils/termin.jsx) — aynı gösterim
// İnşaat Takip ekranındaki TerminCell'de de kullanılıyor.
const terminIpucuFor = (record) => ortakTerminIpucu(
  [
    ['Görev alındı', record['Görev Alınma Tarihi']],
    ...(record['Süreç Durumu'] === 'Enerji Sürecinde' && record['Enerji Adım Kodu']
      ? [[`${ENERJI_ADIM_AD[record['Enerji Adım Kodu']] || record['Enerji Adım Kodu']} adımı başladı`,
          record['Adım Başlangıç']]]
      : []),
    ...(record['Süreç Durumu'] === 'Enerji Sürecinde'
      ? [['Enerji süreci başlangıcı', record['Enerji Başlangıç Tarihi']]]
      : []),
    ...(record['Kaynak'] === 'Taşıma' ? [['Son taşıma', record['Son Taşıma Tarihi']]] : []),
  ],
  terminGunu(record),
);

// Bir kayıt için geçerli termin süresi (gün).
const terminGunu = (record) => {
  if (record['Süreç Durumu'] === 'Enerji Sürecinde') {
    const adim = record['Enerji Adım Kodu'];
    if (adim && ENERJI_ADIM_TERMIN[adim] != null) return ENERJI_ADIM_TERMIN[adim];
  }
  return TERMIN_GUN;
};

const RENK_HEX = {
  green: '#52c41a',
  lime: '#a0d911',
  gold: '#faad14',
  orange: '#fa8c16',
  red: '#f5222d',
  default: '#d9d9d9',
};

// Bir tarihin bugüne göre kaç tam gün geçtiğini döndürür (null = geçersiz)
const gecenGun = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.floor((bugun.getTime() - d.getTime()) / 86400000);
};

// Sürecin termininin başladığı (anchor) tarih — aşama bazlı.
// Enerji aşamasında öncelik: adımın kendi başlangıcı > backend'in hesapladığı
// enerji başlangıcı > görev alınma tarihi.
//
// NOT: Buraya doğrudan `Kurulum Tarihi` YAZILMAMALI. kurulum_bilgileri lokasyon
// başına tek satır tuttuğu için, daha önce kabin kurulmuş bir lokasyona EK
// KURULUM açıldığında o alan yıllar öncesini gösteriyor ve gecikme binlerce güne
// çıkıyordu (ör. 1693 gün). Backend artık GREATEST(kurulum, görev alınma)
// değerini 'Enerji Başlangıç Tarihi' olarak veriyor.
const terminAnchor = (record) => {
  if (record['Süreç Durumu'] !== 'Enerji Sürecinde') {
    return record['Görev Alınma Tarihi'];
  }
  const anchor =
    record['Adım Başlangıç'] ||
    record['Enerji Başlangıç Tarihi'] ||
    record['Görev Alınma Tarihi'];

  // Savunma amaçlı clamp: backend migration'ı henüz uygulanmadıysa eski
  // 'Kurulum Tarihi' gelebilir. Görev alınma tarihinden eski bir anchor,
  // önceki kurulum turuna aittir — asla kullanılmaz.
  const gorev = record['Görev Alınma Tarihi'];
  if (anchor && gorev) {
    const a = new Date(anchor);
    const g = new Date(gorev);
    if (!isNaN(a.getTime()) && !isNaN(g.getTime()) && g > a) return gorev;
  }
  return anchor;
};

// Termin renk skalası: yaklaştıkça sarı, geçince kırmızı
const terminBilgisi = (record) => {
  if (record['Süreç Durumu'] === 'Tamamlandı') {
    return { color: 'green', text: 'Tamamlandı', percent: 100, siralama: -100000 };
  }
  if (record['Süreç Durumu'] === 'Enerji Reddedildi' || record['Süreç Durumu'] === 'Enerji İptal') {
    return { color: 'red', text: record['Süreç Durumu'], percent: 100, siralama: -100001 };
  }
  const anchor = terminAnchor(record);
  const gecen = gecenGun(anchor);
  if (gecen === null) {
    return { color: 'default', text: '-', percent: 0, siralama: -99999 };
  }
  const terminGun = terminGunu(record);
  const kalan = terminGun - gecen; // termin bitimine kalan gün
  const gecikme = gecen - (terminGun + TERMIN_HAK); // ek hak dahil gecikme
  const percent = Math.min(100, Math.round((gecen / (terminGun + TERMIN_HAK)) * 100));

  let color;
  let text;
  if (gecikme > 0) {
    color = 'red';
    text = `${gecikme} gün gecikme`;
  } else if (gecen >= terminGun) {
    color = 'orange';
    text = 'Son gün (ek hak)';
  } else if (kalan <= 2) {
    color = 'gold';
    text = `${kalan} gün kaldı`;
  } else if (kalan <= 4) {
    color = 'lime';
    text = `${kalan} gün kaldı`;
  } else {
    color = 'green';
    text = `${kalan} gün kaldı`;
  }
  // sıralama: gecikenler en üstte (büyük değer), sonra termine yakın olanlar
  return { color, text, percent, siralama: gecen };
};

/**
 * Genel süreç takibi (inşaat + enerji birleşik görünüm).
 *
 * @param {boolean} gomulu - Sekme içinde gösteriliyorsa dış dolgu/başlık kırpılır
 *   (İnşaat & Enerji ekranındaki "Genel Takip" sekmesi bu modda kullanır).
 */
const SurecTakipPage = ({ gomulu = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [surecFilter, setSurecFilter] = useState('Tümü');
  // Kaynak: iş ya bir inşaat talebinden ya da kabin taşımasından geliyor.
  const [kaynakFilter, setKaynakFilter] = useState('Tümü');
  const [onayModalOpen, setOnayModalOpen] = useState(false);
  const [onayRecord, setOnayRecord] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE_URL}/list/execute-query/surec_takip`, {
        params: {},
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      message.error(`Süreç verileri yüklenemedi: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRows = useMemo(() => {
    let data = rows;
    if (surecFilter !== 'Tümü') {
      data = data.filter(r => anaGrup(r['Süreç Durumu']) === surecFilter);
    }
    if (kaynakFilter !== 'Tümü') {
      data = data.filter(r => (r['Kaynak'] || 'Talep') === kaynakFilter);
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLocaleLowerCase('tr');
      data = data.filter(r =>
        (r['Lokasyon Adı'] || '').toLocaleLowerCase('tr').includes(q) ||
        (r['Proje'] || '').toLocaleLowerCase('tr').includes(q) ||
        String(r.maptriksID || '').includes(q)
      );
    }
    return data;
  }, [rows, surecFilter, kaynakFilter, searchText]);

  const surecSayilari = useMemo(() => {
    const sayilar = { 'İnşaat Sürecinde': 0, 'Enerji Sürecinde': 0, 'Tamamlandı': 0 };
    rows.forEach(r => {
      const g = anaGrup(r['Süreç Durumu']);
      if (sayilar[g] !== undefined) sayilar[g] += 1;
    });
    return sayilar;
  }, [rows]);

  const columns = [
    // Liste "Tarih" ve "Sorumlu" ile başlar; varsayılan sıralama tarihe göre
    // azalan (en son atanan iş en üstte).
    {
      title: 'Görev Alınma',
      dataIndex: 'Görev Alınma Tarihi',
      key: 'gorevAlinma',
      width: 130,
      render: formatDate,
      defaultSortOrder: 'descend',
      sorter: (a, b) => zamanDegeri(a['Görev Alınma Tarihi']) - zamanDegeri(b['Görev Alınma Tarihi']),
    },
    {
      title: 'Aktif Sorumlu',
      dataIndex: 'Aktif Sorumlu',
      key: 'sorumlu',
      width: 160,
      sorter: (a, b) => (a['Aktif Sorumlu'] || '').localeCompare(b['Aktif Sorumlu'] || '', 'tr'),
      render: (text) => text || '-',
    },
    {
      title: 'Kaynak',
      dataIndex: 'Kaynak',
      key: 'kaynak',
      width: 90,
      render: (value) => (
        <Tag color={value === 'Taşıma' ? 'purple' : 'default'}>{value || 'Talep'}</Tag>
      ),
    },
    {
      title: 'Lokasyon Kodu',
      dataIndex: 'maptriksID',
      key: 'maptriksID',
      width: 120,
      sorter: (a, b) => String(a.maptriksID || '').localeCompare(String(b.maptriksID || ''), 'tr'),
    },
    {
      title: 'Lokasyon',
      dataIndex: 'Lokasyon Adı',
      key: 'lokasyon',
      sorter: (a, b) => (a['Lokasyon Adı'] || '').localeCompare(b['Lokasyon Adı'] || '', 'tr'),
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Proje',
      dataIndex: 'Proje',
      key: 'proje',
      sorter: (a, b) => (a['Proje'] || '').localeCompare(b['Proje'] || '', 'tr'),
    },
    {
      title: 'Termin',
      key: 'termin',
      width: 150,
      sorter: (a, b) => terminBilgisi(a).siralama - terminBilgisi(b).siralama,
      render: (_, record) => {
        const t = terminBilgisi(record);
        if (t.text === '-' || record['Süreç Durumu'] === 'Tamamlandı') {
          return <Tag color={t.color === 'default' ? undefined : t.color}>{t.text}</Tag>;
        }
        return (
          <Tooltip title={terminIpucuFor(record)}>
            <div style={{ minWidth: 120 }}>
              <Tag color={t.color} style={{ marginBottom: 4 }}>{t.text}</Tag>
              <Progress
                percent={t.percent}
                showInfo={false}
                size="small"
                strokeColor={RENK_HEX[t.color] || RENK_HEX.default}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Kurulum Tarihi',
      dataIndex: 'Kurulum Tarihi',
      key: 'kurulum',
      width: 130,
      render: formatDate,
      sorter: (a, b) => new Date(a['Kurulum Tarihi'] || 0) - new Date(b['Kurulum Tarihi'] || 0),
    },
    {
      title: 'Enerji Verilme Tarihi',
      dataIndex: 'Enerji Verilme Tarihi',
      key: 'enerji',
      width: 160,
      render: formatDate,
      sorter: (a, b) => new Date(a['Enerji Verilme Tarihi'] || 0) - new Date(b['Enerji Verilme Tarihi'] || 0),
    },
    {
      title: 'Süreç Durumu',
      dataIndex: 'Süreç Durumu',
      key: 'surec',
      width: 170,
      sorter: (a, b) => (a['Süreç Durumu'] || '').localeCompare(b['Süreç Durumu'] || '', 'tr'),
      render: (value) => {
        const conf = SUREC_RENK[value] || { color: 'default', icon: null };
        return <Tag color={conf.color} icon={conf.icon}>{value}</Tag>;
      },
    },
    {
      title: 'İşlem',
      key: 'detay',
      width: 110,
      render: (_, record) => (
        <Space size={0}>
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            title="Süreç tarih detayları"
            onClick={() => setDetailRecord(record)}
          />
          {record.talepId && YS_ONAY_DURUMLARI.has(record['Süreç Durumu']) && (
            <Button
              type="text"
              icon={<AuditOutlined />}
              title="Yer sahibi onay / red / beklemeye al"
              onClick={() => {
                setOnayRecord(record);
                setOnayModalOpen(true);
              }}
            />
          )}
          <Button
            type="text"
            icon={<EyeOutlined />}
            title="Lokasyon detayına git"
            onClick={() => navigate(`/detay/${record.maptriksID}`)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: gomulu ? 0 : 16 }}>
      <Modal
        title={`Süreç Detayı${detailRecord?.['Lokasyon Adı'] ? ` — ${detailRecord['Lokasyon Adı']}` : ''}`}
        open={Boolean(detailRecord)}
        onCancel={() => setDetailRecord(null)}
        footer={null}
        destroyOnHidden
      >
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Görev Alınma Tarihi">{formatDate(detailRecord?.['Görev Alınma Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="Plan Tarihi">{formatDate(detailRecord?.['Plan Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="İşlem Tarihi">{formatDate(detailRecord?.['İşlem Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="Kurulum Tarihi">{formatDate(detailRecord?.['Kurulum Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="Enerji Verilme Tarihi">{formatDate(detailRecord?.['Enerji Verilme Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="YS Karar Tarihi">{formatDate(detailRecord?.['YS Karar Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="Aktif Sorumlu">{detailRecord?.['Aktif Sorumlu'] || '-'}</Descriptions.Item>
          <Descriptions.Item label="Süreç Durumu">{detailRecord?.['Süreç Durumu'] || '-'}</Descriptions.Item>
          <Descriptions.Item label="Enerji Başlangıcı">{formatDate(detailRecord?.['Enerji Başlangıç Tarihi'])}</Descriptions.Item>
          <Descriptions.Item label="Enerji Adımı">
            {detailRecord?.['Enerji Adım Kodu']
              ? `${ENERJI_ADIM_AD[detailRecord['Enerji Adım Kodu']] || detailRecord['Enerji Adım Kodu']}` +
                (detailRecord?.['Adım Durumu'] ? ` — ${detailRecord['Adım Durumu']}` : '')
              : detailRecord?.['Enerji Adımı'] || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Adım Başlangıcı">{formatDate(detailRecord?.['Adım Başlangıç'])}</Descriptions.Item>
          <Descriptions.Item label="Enerji Red Sebebi">{detailRecord?.['Enerji Red Sebebi'] || '-'}</Descriptions.Item>
          <Descriptions.Item label="Enerji Notları">
            <span style={{ whiteSpace: 'pre-wrap' }}>{detailRecord?.['Enerji Notları'] || '-'}</span>
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      {/* antd 5'te bordered deprecated; variant kullanilir. Gomulu modda govde
          dolgusu da kirpilir, aksi halde sekme icinde tablo iceri kacar. */}
      <Card
        className={darkMode ? 'dark-card' : ''}
        variant={gomulu ? 'borderless' : 'outlined'}
        styles={gomulu ? { body: { padding: 0 } } : undefined}
        title={
          <Space>
            <ApartmentOutlined />
            {!gomulu && <Title level={4} style={{ margin: 0 }}>Genel Süreç Takibi</Title>}
            <Tag color="orange">İnşaat: {surecSayilari['İnşaat Sürecinde']}</Tag>
            <Tag color="blue">Enerji: {surecSayilari['Enerji Sürecinde']}</Tag>
            <Tag color="green">Tamamlanan: {surecSayilari['Tamamlandı']}</Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Segmented
              options={['Tümü', 'İnşaat Sürecinde', 'Enerji Sürecinde', 'Tamamlandı']}
              value={surecFilter}
              onChange={setSurecFilter}
            />
            <Segmented
              options={['Tümü', 'Talep', 'Taşıma']}
              value={kaynakFilter}
              onChange={setKaynakFilter}
            />
            <Input
              allowClear
              placeholder="Lokasyon / proje ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        }
      >
        <Table
          rowKey="maptriksID"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={filteredRows}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Toplam ${t} lokasyon` }}
        />
      </Card>

      <YsOnayModal
        open={onayModalOpen}
        record={onayRecord}
        onClose={() => setOnayModalOpen(false)}
        onSuccess={fetchData}
      />

    </div>
  );
};

export default SurecTakipPage;
